import { Injectable } from "@angular/core";
import { bugReportStatus } from "../enum/bugReport-status";
import { CampaignStatusEnum } from "../enum/campaign-status";
import { PricingMethod } from "../enum/pricing-method";
import { RouterStatus } from "../enum/router-status";
import { LocationAvailabilityEnum } from "../enum/location-availability";
import { LocationPartnerStatusEnumMapping } from "../enum/locationPartnerStatus";
import { RemunerationEnum } from "../enum/renumeration.enum";
import { PartnerStatus } from "../enum/partner-status";
import { InvoiceStatusEnum } from "../enum/invoicesStatus";
import { CommandeStatusEnum } from "../enum/commandeStatus";
import { PaymentFrequency } from "../enum/payment-frequency";
import { DiscountTypeEnum } from "../enum/discount-type.enum";
import { AdvertiserStatusEnum } from "../enum/advertiser-status";
import { UserHistoryActionsEnum } from "../enum/userHistoryActions";
import { VoucherStatus } from "../enum/VoucherStatus";
import { UserStatus } from "../enum/user-status";
import { PromoteOptions } from "../enum/franchise/promoteOptions";

import { FranchiseStatus } from "../enum/franchise-status.enum";

const LOCATION_AVAILABILITY_ICONS: Partial<Record<LocationAvailabilityEnum, string>> = {
  [LocationAvailabilityEnum.CURRENTLY_INSTALLING]: "icofont-tools-alt-2",
  [LocationAvailabilityEnum.MAINTENANCE]: "icofont-gear",
  [LocationAvailabilityEnum.TEMPORARILY_SUSPENDED]: "icofont-ui-pause",
};

const LOCATION_AVAILABILITY_LABEL_KEYS: Partial<Record<LocationAvailabilityEnum, string>> = {
  [LocationAvailabilityEnum.TEMPORARILY_SUSPENDED]: LocationPartnerStatusEnumMapping.TEMPORARILY_SUSPENDED,
  [LocationAvailabilityEnum.MAINTENANCE]: LocationPartnerStatusEnumMapping.MAINTENANCE,
};

@Injectable({
  providedIn: "root",
})
export class BadgeService {
  constructor() {}

  getBadgeByRouterStatus(status: RouterStatus): string {
    switch (status as unknown as keyof typeof RouterStatus) {
      case RouterStatus.ONLINE.toString():
        return "badge-light-blue";
      case RouterStatus.OUTOFORDER.toString():
        return "badge-light-warning";
      case RouterStatus.ON_SITE:
        return "badge-light-success";
      case RouterStatus.READY_TO_GO:
        return "badge-light-primary";
      case RouterStatus.IN_STOCK:
        return "badge-light-info";
      default:
        return "badge-light-primary";
    }
  }

  getBadgeForAdvertisingCampaingStatus(status: CampaignStatusEnum): string {
    switch (status as unknown as keyof typeof CampaignStatusEnum) {
      case CampaignStatusEnum.ONGOING.toString():
        return "badge-light-info";
      case CampaignStatusEnum.WAITING.toString():
        return "badge-light-warning";
      case CampaignStatusEnum.FINISHED.toString():
        return "badge-light-success";
      case CampaignStatusEnum.DRAFT.toString():
        return "badge-light-gray";
      case CampaignStatusEnum.AWAITING_APPROVAL.toString():
        return "badge-light-blue";
      case CampaignStatusEnum.AWAITING_PAYMENT.toString():
        return "badge-light-primary";
      case CampaignStatusEnum.CANCELED.toString():
      case CampaignStatusEnum.CANCELED_BY_ADMIN.toString():
      case CampaignStatusEnum.CANCELED_BY_FRANCHISE.toString():
        return "badge-light-danger";
      default:
        return "badge-light-info";
    }
  }

  getBadgeByLocationPartnerPricingMethod(status: PricingMethod) {
    switch (status as unknown as keyof typeof PricingMethod) {
      case PricingMethod.VIEWS.toString():
        return "badge-light-info";
      default:
        return "badge-light-gray";
    }
  }

  getBadgeForLocationPartnerAvailability(
    availabilityStatus: LocationAvailabilityEnum
  ): string {
    switch (availabilityStatus) {
      case LocationAvailabilityEnum.AVAILABLE:
        return "badge-light-success";
      case LocationAvailabilityEnum.NOT_AVAILABLE:
        return "badge-light-danger";
      case LocationAvailabilityEnum.CURRENTLY_INSTALLING:
        return "badge-light-info";
      case LocationAvailabilityEnum.PARTIALLY:
        return "badge-light-yellow";
      case LocationAvailabilityEnum.PARTIALLY_BOOKED:
        return "badge-light-blue";
      case LocationAvailabilityEnum.TEMPORARILY_SUSPENDED:
        return "badge-light-gray";
      case LocationAvailabilityEnum.MAINTENANCE:
        return "badge-light-warning";
      default:
        return "badge-light-gray";
    }
  }

  getIconForLocationPartnerAvailability(
    availabilityStatus: LocationAvailabilityEnum
  ): string | undefined {
    return LOCATION_AVAILABILITY_ICONS[availabilityStatus];
  }

  getLabelKeyForLocationPartnerAvailability(
    availabilityStatus: LocationAvailabilityEnum
  ): string {
    return (
      LOCATION_AVAILABILITY_LABEL_KEYS[availabilityStatus] ??
      "enumMapping.locationAvailibility." + availabilityStatus
    );
  }

  getBadgeByAdvertiserStatus(status: AdvertiserStatusEnum) {
    switch (status as unknown as keyof typeof AdvertiserStatusEnum) {
      case AdvertiserStatusEnum.EMAIL_VERIFICATION.toString():
        return "badge-light-warning";
      case AdvertiserStatusEnum.ENABLED.toString():
        return "badge-light-info";
      case AdvertiserStatusEnum.APPROVAL.toString():
        return "badge-light-primary";
      case AdvertiserStatusEnum.DISABLED.toString():
        return "badge-light-danger";
      default:
        return "badge-light-success";
    }
  }
  getBadgeByBugReportStatus(status: bugReportStatus) {
    switch (status as unknown as keyof typeof bugReportStatus) {
      case bugReportStatus.IN_PROGRESS:
        return "badge-light-info";
      default:
        return "badge-light-primary";
    }
  }
  getBadgeByOrderStatus(status: CampaignStatusEnum | null): string {
    switch (status as unknown as keyof typeof CampaignStatusEnum) {
      case CampaignStatusEnum.DRAFT.toString():
        return "badge-light-warning";
      case CampaignStatusEnum.AWAITING_APPROVAL.toString():
        return "badge-light-blue";
      case CampaignStatusEnum.AWAITING_PAYMENT.toString():
        return "badge-light-primary";
      case CampaignStatusEnum.CANCELED.toString():
        return "badge-light-danger";
      case null:
        return "badge-light-gray";
      default:
        return "badge-light-success";
    }
  }

  getBadgeForLPRemuneration(remuneration: RemunerationEnum) {
    switch (remuneration) {
      case RemunerationEnum.FULLY_REMUNERATED:
        return "badge-light-info";
      case RemunerationEnum.PARTIALLY_REMUNERATED:
        return "badge-light-warning";
      case RemunerationEnum.PILOT_PHASE || RemunerationEnum.NOT_REMUNERATED:
        return "badge-light-primary";
      case RemunerationEnum.INTERNET_PROVIDER_PAYMENT:
        return "badge-light-blue";
      default:
        return "badge-light-primary";
    }
  }

  getBadgeByLocationPartnerStatus(status: PartnerStatus) {
    switch (status as unknown as keyof typeof PartnerStatus) {
      case PartnerStatus.INTERNET_PROVIDER_PAYMENT:
        return "badge-light-info";
      case PartnerStatus.REVENUE_SHARING:
        return "badge-light-primary";
      default:
        return "badge-light-warning";
    }
  }
  getBadgeByLocationPartner() {
    return "badge-light-info";
  }
  getBadgeIberisStatus(status: CommandeStatusEnum) {
    switch (status) {
      case CommandeStatusEnum.DRAFT:
        return "badge-light-warning";
      case CommandeStatusEnum.AWAITING_PAYMENT:
        return "badge-light-primary";
      /*case CommandeStatusEnum.PAYÉ:
        return "badge-light-primary";*/
      default:
        return "badge-light-primary";
    }
  }

  getBadgeFactureIberisStatus(status: InvoiceStatusEnum) {
    switch (status as unknown as keyof typeof InvoiceStatusEnum) {
      case InvoiceStatusEnum.EN_RETARD:
        return "badge-light-warning";
      case InvoiceStatusEnum.VALIDE:
        return "badge-light-info";
      default:
        return "badge-light-primary";
    }
  }
  getBadgeByLocationPartnerPaymentFrequency(
    paymentFrequency: PaymentFrequency
  ) {
    switch (paymentFrequency as unknown as keyof typeof PaymentFrequency) {
      case PaymentFrequency.ANNUAL:
        return "badge-light-primary";
      case PaymentFrequency.MONTHLY:
        return "badge-light-info";
      case PaymentFrequency.QUARTERLY:
        return "badge-light-warning";
      case PaymentFrequency.SEMI_ANNUALLY:
        return "badge-light-blue";
      default:
        return "badge-light-gray";
    }
  }

  getBadgeForDiscountType(discount: DiscountTypeEnum) {
    switch (discount) {
      case DiscountTypeEnum.PERCENTAGE:
        return "badge-light-primary";
      case DiscountTypeEnum.AMOUNT:
        return "badge-light-info";
      default:
        return "badge-light-danger";
    }
  }
  getNoneType() {
    return "badge-light-info";
  }

  getBadgeForActionLogger(action: UserHistoryActionsEnum): string {
    switch (action as unknown as keyof typeof UserHistoryActionsEnum) {
      case UserHistoryActionsEnum.GET.toString():
        return "badge-light-info";
      case UserHistoryActionsEnum.DOWNLOAD.toString():
        return "badge-light-warning";
      case UserHistoryActionsEnum.POST.toString():
        return "badge-light-success";
      case UserHistoryActionsEnum.UPLOAD.toString():
        return "badge-light-gray";
      case UserHistoryActionsEnum.PATCH.toString():
        return "badge-light-primary";
      case UserHistoryActionsEnum.DELETE.toString():
        return "badge-light-danger";
      default:
        return "badge-light-info";
    }
  }

  getBadgeForVouchersStatus(voucher: VoucherStatus) {
    switch (voucher) {
      case VoucherStatus.VALID:
        return "badge-light-success";
      case VoucherStatus.EXPIRED:
        return "badge-light-danger";
      case VoucherStatus.VOUCHER_CAPACITY_EXCEEDED:
        return "badge-light-danger";
      default:
        return "badge-light-danger";
    }
  }

  getBadgeForUserStatus(userStatus: UserStatus) {
    switch (userStatus) {
      case UserStatus.ACTIVE:
        return "badge-light-success";
      case UserStatus.ARCHIVED:
        return "badge-light-danger";
      default:
        return "badge-light-info";
    }
  }

  getPromoteOptionsStatus(option: PromoteOptions, isItemIncluded: boolean) {
    if (
      option === PromoteOptions.ALL ||
      (isItemIncluded && option === PromoteOptions.CUSTOMIZED)
    ) {
      return "badge-light-success";
    } else {
      return "badge-light-primary";
    }
  }

  getBadgeByLocationPartnerActiveStatus(franchiseStatus: FranchiseStatus) {
    switch (franchiseStatus) {
      case FranchiseStatus.ACTIVE:
        return "badge-light-success";
      case FranchiseStatus.AWAITING_CONFIRMATION:
        return "badge-light-primary";
      default:
        return "badge-light-primary";
    }
  }
}
